import { useState } from "react";
import moment from "moment";

import CalendarMonth, { monthOf, shiftMonth, startOfDay } from "./CalendarMonth";
import usePopover from "./usePopover";
import "../field-popovers.css";

/**
 * A date field that opens the app's own calendar (the CalendarMonth the work
 * order list jumps days with) instead of the browser's date control. Sits
 * inside an .rf-input-shell.
 *
 * The app stores dates as DD/MM/YYYY; `value` and `onChange` speak that.
 */
const pad = (n) => String(n).padStart(2, "0");

function toStamp(ddmmyyyy) {
  const [d, m, y] = String(ddmmyyyy || "").split("/").map(Number);
  if (!d || !m || !y) return null;
  const t = new Date(y, m - 1, d).getTime();
  return isNaN(t) ? null : t;
}
function toDDMMYYYY(stamp) {
  const d = new Date(stamp);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function DateField({ value, onChange, disabled, placeholder }) {
  const stamp = toStamp(value);
  const pop = usePopover(330);
  const [viewMonth, setViewMonth] = useState(() => monthOf(stamp || Date.now()));

  function pick(t) {
    onChange(toDDMMYYYY(t));
    pop.hide();
  }

  return (
    <div className="rf-fp" ref={pop.ref}>
      <button
        type="button"
        className="rf-fp-btn"
        disabled={disabled}
        aria-expanded={pop.open}
        onClick={() => {
          if (pop.open) return pop.hide();
          setViewMonth(monthOf(stamp || Date.now()));
          pop.show();
        }}
      >
        <span className={`rf-fp-value rf-num${stamp ? "" : " is-empty"}`}>
          {stamp ? moment(stamp).format("ddd D MMM YYYY") : placeholder || "Pick a date"}
        </span>
        <span className="rf-fp-icon rf-fp-cal" aria-hidden="true" />
      </button>

      {pop.open && (
        <div className={`rf-fp-pop rf-fp-cal-pop${pop.openUp ? " is-up" : ""}`}>
          <CalendarMonth
            year={viewMonth.year}
            monthIndex={viewMonth.monthIndex}
            from={stamp}
            to={stamp}
            onPick={pick}
            onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
            onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
          />
          <div className="rf-fp-foot">
            <button type="button" onClick={() => pick(startOfDay(Date.now()))}>
              Today
            </button>
            {stamp && (
              <button
                type="button"
                className="is-quiet"
                onClick={() => {
                  onChange("");
                  pop.hide();
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
